import React, { useEffect, useMemo, useState } from 'react';

import type { UiFormFieldOption, UiFormFieldSpec } from '../config/uiSpec';
import { usePericlesClient } from '../core/PericlesProvider';

import { CheckboxInput } from './CheckboxInput';
import { DateInput } from './DateInput';
import { NumberInput } from './NumberInput';
import { SelectInput } from './SelectInput';
import { TextInput } from './TextInput';

export interface FieldInputProps {
  field: UiFormFieldSpec;
  value: unknown;
  onChange: (nextValue: unknown) => void;
  disabled?: boolean;
}

const textareaClasses = [
  'block w-full rounded-md border border-slate-300 px-3 py-2 text-sm',
  'shadow-sm focus:border-slate-500 focus:ring-1 focus:ring-slate-500',
  'disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400',
].join(' ');

function inferRelatedEntity(fieldName: string): string | null {
  const normalized = fieldName.replace(/Id$/i, '').trim();

  if (!normalized) {
    return null;
  }

  return normalized.toLowerCase();
}

function DynamicSelectField({ field, value, onChange, disabled }: FieldInputProps): JSX.Element {
  const { baseUrl, fetcher } = usePericlesClient();
  const [options, setOptions] = useState<UiFormFieldOption[]>(field.options ?? []);

  useEffect(() => {
    setOptions(field.options ?? []);
  }, [field.options]);

  const relatedEntity = useMemo(() => {
    if ((field.options?.length ?? 0) > 0) {
      return null;
    }

    return inferRelatedEntity(field.field);
  }, [field.field, field.options]);

  const pickString = (val: unknown): string | undefined =>
    typeof val === 'string' || typeof val === 'number' ? String(val) : undefined;

  useEffect(() => {
    if (!relatedEntity) return;

    let isCancelled = false;

    const loadOptions = async () => {
      try {
        const base = baseUrl.replace(/\/$/, '');
        const response = await fetcher(
          `${base}/entities/${encodeURIComponent(relatedEntity)}?page=0&pageSize=50`
        );

        if (!response.ok) {
          return;
        }

        const json = await response.json();
        const items = Array.isArray(json?.items) ? json.items : [];

        const mapped = items
          .map((entity: unknown) => {
            if (!entity || typeof entity !== 'object') {
              return null;
            }

            const candidate = entity as Record<string, unknown>;
            const rawId = candidate.id ?? candidate.value ?? candidate.key;
            const normalizedId = pickString(rawId);

            if (!normalizedId) {
              return null;
            }

            const label =
              pickString(candidate.name) ??
              pickString(candidate.title) ??
              pickString(candidate.code) ??
              pickString(candidate.label) ??
              `#${normalizedId}`;

            return { value: normalizedId, label };
          })
          .filter(Boolean) as UiFormFieldOption[];

        if (!isCancelled) {
          setOptions(mapped as NonNullable<typeof mapped[0]>[]);
        }
      } catch (err) {
        console.error('Failed to load options for', field.field, err);
      }
    };

    void loadOptions();

    return () => {
      isCancelled = true;
    };
  }, [baseUrl, fetcher, field.field, relatedEntity]);

  return (
    <SelectInput
      name={field.field}
      disabled={disabled}
      value={(value as string | undefined) ?? ''}
      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onChange(e.target.value || undefined)}
    >
      <option value="">--</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </SelectInput>
  );
}

export function renderFieldInput({
  field,
  value,
  onChange,
  disabled,
}: FieldInputProps): JSX.Element {
  const common = {
    name: field.field,
    disabled,
  };

  const placeholder = field.placeholder;

  switch (field.editor) {
    case 'number':
      return (
        <NumberInput
          {...common}
          value={(value as number | string | undefined) ?? ''}
          placeholder={placeholder}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange(e.target.value === '' ? undefined : Number(e.target.value))
          }
        />
      );
    case 'date':
      return (
        <DateInput
          {...common}
          value={(value as string | undefined) ?? ''}
          placeholder={placeholder}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value || undefined)}
        />
      );
    case 'select':
      return <DynamicSelectField field={field} value={value} onChange={onChange} disabled={disabled} />;
    case 'checkbox':
      return (
        <CheckboxInput
          {...common}
          checked={Boolean(value)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
        />
      );
    case 'textarea':
      return (
        <textarea
          {...common}
          className={textareaClasses}
          placeholder={placeholder}
          value={(value as string | undefined) ?? ''}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
        />
      );
    case 'text':
    default:
      return (
        <TextInput
          {...common}
          placeholder={placeholder}
          value={value as string | undefined}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        />
      );
  }
}
